// Track 4.4 — Claude vision keyframe analysis.
//
// On position change (and at most every 30s steady-state), grab a downsampled
// JPEG of the current frame and ship it to the aero-ai service. The server
// asks Claude vision for one sentence about what to physically change,
// caches by perceptual hash, and enforces a per-day quota. We stream the
// answer into the same proactive observations feed the coach beats use.
//
// Why a separate hook from useAeroCoachTriggers? Different I/O profile:
//   - coach beats: text-only, fast (<1s), 1 per 20s overall
//   - keyframe analysis: image payload (~80 KB), 2-4s Claude vision latency,
//     much more expensive per call ($)
// Combining them would force the rate limiter to either be too restrictive
// for coach beats or too permissive for vision, so they stay split.

import { useEffect, useRef } from 'react';
import { captureVideoFrameJpegBase64 } from '../services/visionAdvanced';

interface Options {
  wsUrl: string;
  userId?: string;
  sessionId?: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  /** Current classified position. Change → trigger an immediate keyframe. */
  positionClass: string | null;
  /** Vision context to attach to the keyframe payload so Claude sees the
   *  numbers, not just the picture. */
  getContext?: () => Record<string, any> | undefined;
  locale?: string;
  /** Surface returned observation into the AeroAiPanel feed. */
  onObservation?: (msg: { id: string; content: string; streaming: boolean }) => void;
  /** Gate the whole hook — used to wait until baseline locks. */
  enabled?: boolean;
}

// Cadence floor. Keep this distinct from the coach beat 20s window so we
// don't double-fire on every position change.
const STEADY_INTERVAL_MS = 30_000;
const MIN_GAP_MS = 8_000;

export function useKeyframeCoach(opts: Options): void {
  const {
    wsUrl,
    userId = '',
    sessionId = '',
    videoRef,
    positionClass,
    locale,
    enabled = true,
  } = opts;

  const getContextRef = useRef(opts.getContext);
  const onObservationRef = useRef(opts.onObservation);
  useEffect(() => {
    getContextRef.current = opts.getContext;
    onObservationRef.current = opts.onObservation;
  }, [opts.getContext, opts.onObservation]);

  const wsRef = useRef<WebSocket | null>(null);
  const lastSentAtRef = useRef<number>(0);
  const lastPositionRef = useRef<string | null>(null);
  const currentIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          const id = currentIdRef.current;
          if (!id) return;
          if (msg.type === 'token') {
            onObservationRef.current?.({ id, content: msg.content, streaming: true });
          } else if (msg.type === 'done' || msg.type === 'error') {
            onObservationRef.current?.({ id, content: '', streaming: false });
            currentIdRef.current = null;
          }
        } catch (err) {
          console.warn('keyframe ws parse err', err);
        }
      };
      ws.onclose = () => {
        if (!closed) timer = setTimeout(connect, 2_000);
      };
      ws.onerror = () => {};
    };
    connect();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled, wsUrl]);

  const sendKeyframe = (reason: 'position_change' | 'steady') => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (Date.now() - lastSentAtRef.current < MIN_GAP_MS) return;
    // Don't overlap — wait for current vision response to land before
    // sending a new one. Steady-state ticks are best-effort anyway.
    if (currentIdRef.current) return;
    const video = videoRef.current;
    if (!video) return;
    const jpeg = captureVideoFrameJpegBase64(video, 480, 0.7);
    if (!jpeg) return;
    lastSentAtRef.current = Date.now();
    const id = `kf-${Date.now()}-${reason}`;
    currentIdRef.current = id;
    onObservationRef.current?.({ id, content: '', streaming: true });
    ws.send(
      JSON.stringify({
        type: 'keyframe',
        keyframe_b64: jpeg,
        user_id: userId,
        session_id: sessionId,
        locale,
        context: getContextRef.current?.(),
      }),
    );
  };

  // Position-change trigger.
  useEffect(() => {
    if (!enabled || !positionClass) return;
    if (lastPositionRef.current === null) {
      lastPositionRef.current = positionClass;
      return;
    }
    if (positionClass === lastPositionRef.current) return;
    lastPositionRef.current = positionClass;
    sendKeyframe('position_change');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionClass, enabled]);

  // Steady-state tick.
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => sendKeyframe('steady'), STEADY_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
